import type { PermissionsMap } from "@printflow/auth-shared";
import { ChevronRight, Lock } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta } from "../../components/ui/primitivos";
import {
  actualizarRol,
  crearRol,
  fetchCatalogoPermisos,
  fetchRoles,
  type Rol,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { aCodigos, aMapa, EditorPermisos } from "./EditorPermisos";
import { buscarRol, hayCambios } from "./rol";

/**
 * Alta y edición de un rol, porteadas de `RoleDetailPage.tsx` (ADR 0068).
 *
 * **Son pantallas y no un modal** (ADR 0064): el editor de permisos tiene doce
 * grupos colapsables con hasta ocho interruptores cada uno, y en un cuadro de
 * 512 px con scroll propio no se podía ver el rol y sus permisos a la vez.
 * Allá el reparto es 1 + 2 columnas — los datos a la izquierda, los permisos a
 * la derecha— y es lo que se portea.
 *
 * `/roles/nuevo` y `/roles/:nombre` son la misma pantalla, como allá, donde
 * `/admin/roles/new` y `/admin/roles/:id` montan el mismo componente.
 */
export function RolPagina() {
  const { nombre: nombreEnUrl } = useParams<{ nombre: string }>();
  const navegar = useNavigate();
  const { can } = useSession();

  const esNuevo = nombreEnUrl === undefined;
  // Los roles no tienen visibilidad por fila —quien tiene `roles.read` los ve
  // todos— y son un puñado, así que se busca en el listado en vez de sumar un
  // `GET /roles/:nombre`.
  const roles = useDatos(fetchRoles);
  const catalogo = useDatos(fetchCatalogoPermisos);

  if (roles.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Rol" alVolver={() => navegar("/roles")} />
        <Aviso>{roles.error}</Aviso>
      </div>
    );
  }
  if (!roles.datos) {
    return <p className="p-8 text-center text-neutral-500">Cargando rol…</p>;
  }

  const rol = esNuevo ? null : buscarRol(roles.datos, nombreEnUrl ?? "");
  if (!esNuevo && !rol) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Rol" alVolver={() => navegar("/roles")} />
        <Aviso>No existe un rol con ese nombre.</Aviso>
      </div>
    );
  }

  // Los roles base viven en CÓDIGO y son inmutables (invariante 4): se pueden
  // mirar, y ahí la pantalla es de lectura entera.
  const soloLectura = esNuevo
    ? !can("roles", "create")
    : !rol?.editable || !can("roles", "permisos.update");

  return (
    <Formulario
      // Remontar al cambiar de rol: el formulario arranca su estado de las
      // props, y sin la clave quedaría con los permisos del rol anterior.
      key={rol?.nombre ?? "nuevo"}
      rol={rol ?? null}
      catalogo={catalogo.datos ?? []}
      soloLectura={soloLectura}
      alVolver={() => navegar("/roles")}
      alGuardar={(n) => navegar(`/roles/${encodeURIComponent(n)}`, { replace: true })}
    />
  );
}

function Formulario({
  rol,
  catalogo,
  soloLectura,
  alVolver,
  alGuardar,
}: {
  rol: Rol | null;
  catalogo: Parameters<typeof EditorPermisos>[0]["catalogo"];
  soloLectura: boolean;
  alVolver: () => void;
  alGuardar: (nombre: string) => void;
}) {
  const original = {
    descripcion: rol?.descripcion ?? "",
    permisos: aCodigos(rol?.permisos ?? ({} as PermissionsMap)),
  };

  const [nombre, setNombre] = useState(rol?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(original.descripcion);
  const [permisos, setPermisos] = useState<Set<string>>(() => new Set(original.permisos));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esNuevo = rol === null;
  const cambiado = hayCambios(original, { descripcion, permisos });

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const mapa = aMapa(permisos);
      if (rol) await actualizarRol(rol.nombre, { descripcion, permisos: mapa });
      else await crearRol({ nombre: nombre.trim(), descripcion, permisos: mapa });
      alGuardar(rol?.nombre ?? nombre.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <nav aria-label="Miga de pan" className="flex items-center text-neutral-500 text-sm">
        <Link to="/roles" className="hover:text-neutral-900">
          Roles
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="font-medium text-neutral-900">{rol?.nombre ?? "Nuevo rol"}</span>
      </nav>

      <PageHeader
        titulo={rol?.nombre ?? "Nuevo rol"}
        subtitulo={
          esNuevo
            ? "Creá un rol y elegí qué va a poder hacer"
            : soloLectura
              ? "Rol del sistema: sus permisos viven en código y no se editan"
              : "Datos del rol y sus permisos"
        }
        alVolver={alVolver}
        acciones={
          soloLectura ? undefined : (
            // Como en `imprenta`: al editar, «Guardar» aparece solo cuando hay
            // algo que guardar. Un botón siempre disponible deja de decir nada.
            <Boton
              disabled={guardando || (esNuevo ? nombre.trim().length === 0 : !cambiado)}
              onClick={guardar}
            >
              {guardando ? "Guardando..." : esNuevo ? "Crear rol" : "Guardar rol"}
            </Boton>
          )
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="space-y-3 p-4 lg:col-span-1">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-neutral-900 text-sm">
              Información básica
              {rol && !rol.editable ? (
                <Etiqueta>
                  <span className="inline-flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Sistema
                  </span>
                </Etiqueta>
              ) : null}
            </h2>
            <p className="text-neutral-500 text-sm">
              {esNuevo ? "Nombre y descripción del rol nuevo" : "Datos generales del rol"}
            </p>
          </div>

          {/* El nombre es la CLAVE del rol —el endpoint lo usa en la URL— así
              que se elige una vez y no se cambia. `imprenta` lo deja editable
              porque allá la clave es un id numérico. */}
          <Campo
            etiqueta="Nombre"
            value={nombre}
            disabled={!esNuevo}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Cortador"
            {...(esNuevo ? { ayuda: "No se puede cambiar después: es la clave del rol" } : {})}
          />
          <Campo
            etiqueta="Descripción"
            value={descripcion}
            disabled={soloLectura}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Para qué sirve este rol"
          />
          {rol ? (
            <p className="text-neutral-500 text-sm">
              {rol.tipo === "base" ? "Rol del sistema" : "Rol personalizado"}
            </p>
          ) : null}
        </Card>

        <Card className="space-y-3 p-4 lg:col-span-2">
          <div>
            <h2 className="font-semibold text-neutral-900 text-sm">Permisos</h2>
            <p className="text-neutral-500 text-sm">
              {soloLectura
                ? "Lo que este rol puede hacer"
                : "Elegí qué va a poder hacer quien tenga este rol"}
            </p>
          </div>
          <EditorPermisos
            catalogo={catalogo}
            seleccionados={permisos}
            soloLectura={soloLectura}
            onCambiar={(codigo, activo) =>
              setPermisos((prev) => {
                const siguiente = new Set(prev);
                if (activo) siguiente.add(codigo);
                else siguiente.delete(codigo);
                return siguiente;
              })
            }
          />
        </Card>
      </div>
    </div>
  );
}
